import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';
import { apiFetch, API_BASE, getToken } from '../../services/apiClient';
import { itemEffectiveUnitPrice, unitPer } from './AdminBidDetail';

// NDOT bid estimate (Ryan, Oct 10 2026): one of our bids, line by line, against the NDOT price history.
// A district for the whole bid at the top, any line moved to another; each line shows that district's
// High, Low and Average (last 5 years unless changed) — click one to make it the estimate, or type a
// price (the only way on a line with no history). Saved on its own sheet; the bid's pricing is untouched.
const btn = { padding: '6px 12px', borderRadius: 7, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12.5, fontWeight: 700, color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit' };
const on = { background: '#1e3a5f', borderColor: '#1e3a5f', color: '#fff' };
const inp = { padding: '8px 10px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 13.5, fontFamily: 'inherit', boxSizing: 'border-box' };
const th = { textAlign: 'left', padding: '7px 8px', fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.4, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap', background: '#f9fafb' };
const td = { padding: '6px 8px', fontSize: 13, borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' };
const money = n => (n == null || isNaN(n) ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: Number(n) % 1 ? 2 : 0, maximumFractionDigits: 2 })}`);
const money2 = n => (n == null || isNaN(n) ? '—' : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const dt = s => { if (!s) return ''; const d = new Date(String(s).slice(0, 10) + 'T12:00:00'); return isNaN(d) ? s : d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); };
const itemFmt = no => (no && no.length === 7 ? `${no.slice(0, 3)} ${no.slice(3)}` : no || '');
const DCOLOR = { 1: '#b45309', 2: '#1d4ed8', 3: '#15803d' };
const PICKS = [['high', 'High', '#b91c1c'], ['low', 'Low', '#15803d'], ['average', 'Average', '#1e3a5f']];
const statValue = (s, pick) => (!s || !s.n ? null : pick === 'high' ? s.high.unit_price : pick === 'low' ? s.low.unit_price : pick === 'average' ? s.average : pick === 'median' ? s.median : null);
const tip = (s, pick) => {
  if (!s || !s.n) return '';
  if (pick === 'average') return `Average of ${s.n} prices from ${s.contracts} contract${s.contracts === 1 ? '' : 's'} · median ${money(s.median)}`;
  const x = pick === 'high' ? s.high : s.low;
  return `${x.bidder}${x.rank === 1 ? ' (low bidder)' : ''} · contract ${x.contract_no} · ${dt(x.bid_date)}${x.county ? ` · ${x.county}` : ''}${x.quantity != null ? ` · qty ${x.quantity}` : ''}`;
};

// Why a line has no High / Low / Average, and the one-click way to get some (Ryan, Oct 12 2026: 623 0537
// "is in the system" but 4565's District 2 line showed nothing — its prices were in other districts /
// older years). Also says when Road and Highway Builders' prices were left out.
const linkBtn = { background: 'none', border: 'none', padding: 0, color: '#1d4ed8', fontWeight: 800, fontSize: 11.5, cursor: 'pointer', fontFamily: 'inherit' };
function NoPricesHint({ it, d, years, onDistrict, onAllYears }) {
  if (!it.item_no) return null;
  const c = it.counts || {};
  const here = c[d] || { years: 0, all: 0 };
  const leftOut = it.left_out ? <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{it.left_out} Road &amp; Highway price{it.left_out === 1 ? '' : 's'} left out</div> : null;
  if (here.years > 0) return leftOut;
  const others = [1, 2, 3].filter(n => n !== d && c[n] && c[n].years > 0);
  const nowhere = ![1, 2, 3].some(n => c[n] && c[n].all > 0);
  return (
    <div style={{ fontSize: 11.5, color: '#92400e', marginTop: 3, lineHeight: 1.45 }}>
      {nowhere ? (/^623/.test(it.item_no) ? 'Not on any NDOT bid tab in the history yet — type a price.' : 'Only Section 623 items are kept from the NDOT bid tabs — type a price.') : <>
        No District {d} prices{years ? ` in the last ${years} years` : ''}.
        {years > 0 && here.all > 0 && <> District {d} has {here.all} older — <button type="button" onClick={onAllYears} style={linkBtn}>use all years</button>.</>}
        {others.map(n => <span key={n}> District {n} has {c[n].years} — <button type="button" onClick={() => onDistrict(n)} style={linkBtn}>use District {n}</button>.</span>)}
        {years > 0 && !here.all && !others.length && <> Older ones in other districts — <button type="button" onClick={onAllYears} style={linkBtn}>use all years</button>.</>}
      </>}
      {leftOut}
    </div>
  );
}

function BidPicker({ onPick }) {
  const [q, setQ] = useState('');
  const [list, setList] = useState(null);
  useEffect(() => {
    const t = setTimeout(() => apiFetch(`/ndot-prices/estimate-bids?q=${encodeURIComponent(q)}`).then(setList).catch(() => setList([])), 200);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="admin-card" style={{ padding: 14 }}>
      <div style={{ fontSize: 13.5, fontWeight: 800, color: '#111827', marginBottom: 8 }}>Pick one of our bids to price against the NDOT history</div>
      <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search our bids — 4567, North of Vegas…" style={{ ...inp, width: '100%', maxWidth: 420, marginBottom: 10 }} />
      {list == null ? <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading…</div> : !list.length ? <div style={{ color: '#6b7280', fontSize: 13 }}>No bids match.</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {list.map(b => (
            <button key={b.id} onClick={() => onPick(b.id)} style={{ ...btn, textAlign: 'left', display: 'flex', justifyContent: 'space-between', gap: 10, padding: '9px 12px', whiteSpace: 'normal' }}>
              <span><b style={{ color: '#111827' }}>{b.job_name || 'Bid'}</b>{b.job_location ? <span style={{ color: '#6b7280', fontWeight: 500 }}> · {b.job_location}</span> : null}</span>
              <span style={{ color: '#6b7280', fontWeight: 600, flexShrink: 0 }}>{b.bid_date ? dt(b.bid_date) + ' · ' : ''}{b.items_623} NDOT items{b.estimate_updated_at ? <span style={{ color: '#15803d' }}> · estimate started</span> : null}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function NdotBidEstimate() {
  const [bidId, setBidId] = useState(() => { try { return localStorage.getItem('titan_ndot_est_bid') || null; } catch { return null; } });
  const [data, setData] = useState(null);
  const [years, setYears] = useState(null);
  const [district, setDistrict] = useState(null);
  const [lines, setLines] = useState({});
  const [ours, setOurs] = useState({});
  const [saveState, setSaveState] = useState('');
  const dirty = useRef(false);

  const pickBid = id => { setBidId(id); try { id ? localStorage.setItem('titan_ndot_est_bid', id) : localStorage.removeItem('titan_ndot_est_bid'); } catch { /* per-viewer */ } };

  // Load the bid's lines with the history (years from the saved sheet unless changed here).
  const load = useCallback((id, y) => {
    setData(d => (d && d.bid.id === id ? d : null));
    apiFetch(`/ndot-prices/estimate/${id}${y != null ? `?years=${y}` : ''}`).then(d => {
      setData(d);
      if (y == null) {
        setYears(d.years);
        setDistrict(d.estimate && d.estimate.district ? d.estimate.district : d.district_guess || null);
        setLines(d.estimate ? d.estimate.lines || {} : {});
        dirty.current = false; setSaveState(d.estimate ? `Saved ${new Date(d.estimate.updated_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}${d.estimate.updated_by_name ? ` by ${d.estimate.updated_by_name}` : ''}` : '');
      }
    }).catch(() => { setData({ error: true }); });
  }, []);
  useEffect(() => { if (bidId) load(bidId, null); }, [bidId, load]);

  // Our own price on each line, worked out the way the bid page works it out.
  useEffect(() => {
    if (!bidId) return;
    setOurs({});
    apiFetch(`/bids/${bidId}`).then(bid => {
      const out = {};
      for (const raw of bid.items || []) {
        let it = raw; try { it = { ...raw, sub_items: raw.sub_items ? (typeof raw.sub_items === 'string' ? JSON.parse(raw.sub_items) : raw.sub_items) : [] }; } catch { it = { ...raw, sub_items: [] }; }
        try { out[raw.id] = itemEffectiveUnitPrice(it, bid); } catch { out[raw.id] = null; }
      }
      setOurs(out);
    }).catch(() => setOurs({}));
  }, [bidId]);

  const changeYears = y => { setYears(y); dirty.current = true; load(bidId, y); };

  // The line's district, and its estimate price (a clicked High/Low/Average follows the history; typed stays).
  const lineDistrict = it => (lines[it.id] && lines[it.id].district) || district;
  const linePrice = useCallback(it => {
    const l = lines[it.id]; if (!l) return null;
    if (l.pick && l.pick !== 'manual') { const v = statValue(it.stats && it.stats[(l.district || district)], l.pick); return v != null ? v : null; }
    return l.price != null ? l.price : null;
  }, [lines, district]);

  const setLine = (id, patch) => { dirty.current = true; setLines(ls => ({ ...ls, [id]: { ...(ls[id] || {}), ...patch } })); };
  const pick = (it, p) => {
    const cur = lines[it.id] || {};
    if (cur.pick === p) return setLine(it.id, { pick: null, price: null });     // click again to clear
    setLine(it.id, { pick: p, price: statValue(it.stats[lineDistrict(it)], p) });
  };
  const setDistrictAll = d => { dirty.current = true; setDistrict(d); };
  const setLineDistrict = (it, d) => setLine(it.id, { district: d === district ? null : d });
  const fillAll = p => {
    if (!data) return;
    dirty.current = true;
    setLines(ls => {
      const next = { ...ls };
      for (const it of data.items) {
        const l = next[it.id] || {};
        if (l.pick === 'manual' && l.price != null) continue;                // typed prices stay
        const v = statValue(it.stats && it.stats[(l.district || district)], p);
        if (v != null) next[it.id] = { ...l, pick: p, price: v };
      }
      return next;
    });
  };

  // Save a moment after the last change (and right away before a print).
  const saveNow = useCallback(() => {
    if (!bidId || !data || data.error || !dirty.current) return Promise.resolve();
    const body = { district, years, lines: Object.fromEntries((data.items || []).map(it => [it.id, { ...(lines[it.id] || {}), price: linePrice(it) }]).filter(([, l]) => l.district || l.pick || l.price != null)) };
    return apiFetch(`/ndot-prices/estimate/${bidId}`, { method: 'PUT', body: JSON.stringify(body) })
      .then(r => { dirty.current = false; setSaveState(`Saved ${new Date(r.updated_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`); })
      .catch(e => setSaveState(e.error || 'Could not save'));
  }, [bidId, data, district, years, lines, linePrice]);
  useEffect(() => {
    if (!bidId || !data || data.error || !dirty.current) return;
    setSaveState('Saving…');
    const t = setTimeout(saveNow, 700);
    return () => clearTimeout(t);
  }, [lines, district, years, bidId, data, linePrice, saveNow]);

  const fileName = ext => `NDOT Estimate - ${String((data && data.bid.job_name) || 'bid').replace(/[\\/:*?"<>|]/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 60)}.${ext}`;
  // Print: the house-form PDF (saved first so it matches the screen), opened in a new tab.
  const [printing, setPrinting] = useState(false);
  const printPdf = async () => {
    const w = window.open('', '_blank');
    setPrinting(true);
    try {
      await saveNow();
      const r = await fetch(`${API_BASE}/ndot-prices/estimate/${bidId}/pdf`, { headers: { 'x-worker-token': getToken() } });
      if (!r.ok) throw new Error('print');
      const url = URL.createObjectURL(await r.blob());
      if (w) w.location.href = url;
      else { const a = document.createElement('a'); a.href = url; a.download = fileName('pdf'); a.click(); }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { if (w) w.close(); alert('Could not print the estimate.'); }
    setPrinting(false);
  };
  // Excel: what is on the screen, one row a line, numbers as numbers, the extensions and totals as formulas.
  const exportExcel = () => {
    const USED = { high: 'High', low: 'Low', average: 'Average', median: 'Median', manual: 'Typed' };
    const head = ['Item', 'Description', 'Qty', 'Unit', 'District', 'High', 'Low', 'Average', '# Prices', 'Estimate (unit)', 'Used', 'Extended', 'Our bid (unit)', 'Our bid (extended)'];
    const aoa = [
      [`NDOT Estimate — ${data.bid.job_name || ''}`],
      [[data.bid.job_location, district ? `District ${district}` : 'No district picked', years ? `last ${years} years` : 'all years', `exported ${new Date().toLocaleDateString('en-US')}`].filter(Boolean).join('  ·  ')],
      [],
      head,
    ];
    const first = aoa.length + 1;
    for (const it of data.items) {
      const l = lines[it.id] || {}, d = lineDistrict(it), st = (it.stats && it.stats[d]) || { n: 0 }, pr = linePrice(it), q = Number(it.quantity) || 0, per = unitPer(it.unit);
      const row = aoa.length + 1;
      aoa.push([it.item_no ? `${it.item_no.slice(0, 3)} ${it.item_no.slice(3)}` : (it.pay_item_number || it.item_number || ''), it.description || '', it.quantity ?? '', it.unit || '', d || '',
        st.n ? st.high.unit_price : '', st.n ? st.low.unit_price : '', st.n ? st.average : '', st.n || '',
        pr != null ? pr : '', pr != null ? (USED[l.pick] || '') : '', pr != null ? { t: 'n', f: per > 1 ? `C${row}*J${row}/${per}` : `C${row}*J${row}`, v: Math.round(pr * q / per * 100) / 100 } : '',
        ours[it.id] != null ? Math.round(ours[it.id] * 100) / 100 : '', ours[it.id] != null ? { t: 'n', f: per > 1 ? `C${row}*M${row}/${per}` : `C${row}*M${row}`, v: Math.round(ours[it.id] * q / per * 100) / 100 } : '']);
    }
    const last = aoa.length;
    aoa.push([]);
    aoa.push(['', 'TOTAL', '', '', '', '', '', '', '', '', '', { t: 'n', f: `SUM(L${first}:L${last})`, v: Math.round(totals.est * 100) / 100 }, '', { t: 'n', f: `SUM(N${first}:N${last})`, v: Math.round(totals.ourT * 100) / 100 }]);
    aoa.push(['', `${totals.priced} of ${data.items.length} lines priced. High / Low / Average = other bidders' unit prices on NDOT bid tabs for the line's district.`]);
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 10 }, { wch: 44 }, { wch: 8 }, { wch: 7 }, { wch: 8 }, { wch: 11 }, { wch: 11 }, { wch: 11 }, { wch: 8 }, { wch: 14 }, { wch: 9 }, { wch: 14 }, { wch: 13 }, { wch: 16 }];
    for (let r = first; r <= last + 2; r++) for (const c of ['F', 'G', 'H', 'J', 'L', 'M', 'N']) { const ref = ws[`${c}${r}`]; if (ref && ref.t === 'n') ref.z = '"$"#,##0.00'; }
    const wb = XLSX.utils.book_new(); XLSX.utils.book_append_sheet(wb, ws, 'Estimate');
    XLSX.writeFile(wb, fileName('xlsx'));
  };

  const totals = useMemo(() => {
    if (!data || !data.items) return null;
    let est = 0, ourT = 0, priced = 0, missing = 0, ourAll = true;
    for (const it of data.items) {
      const q = (Number(it.quantity) || 0) / unitPer(it.unit), p = linePrice(it);
      if (p != null) { est += p * q; priced++; } else missing++;
      if (ours[it.id] != null) ourT += ours[it.id] * q; else ourAll = false;
    }
    return { est, ourT, priced, missing, ourAll };
  }, [data, linePrice, ours]);

  if (!bidId) return <BidPicker onPick={pickBid} />;
  if (!data) return <div style={{ color: '#9ca3af', fontSize: 13 }}>Loading the bid…</div>;
  if (data.error) return <div><div style={{ color: '#b91c1c', fontSize: 13, marginBottom: 10 }}>Could not load that bid.</div><button onClick={() => pickBid(null)} style={btn}>Pick another bid</button></div>;

  return (
    <div>
      <div className="admin-card" style={{ padding: 14, marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>{data.bid.job_name}</div>
            <div style={{ fontSize: 12.5, color: '#6b7280' }}>{data.bid.job_location || 'No location on the bid'}{data.bid.bid_date ? ` · bids ${dt(data.bid.bid_date)}` : ''}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, color: saveState.startsWith('Saved') ? '#15803d' : '#6b7280', fontWeight: 700 }}>{saveState}</span>
            <button onClick={printPdf} disabled={printing} style={btn}>{printing ? 'Printing…' : '🖨 Print'}</button>
            <button onClick={exportExcel} style={btn}>⬇ Excel</button>
            <a href={`/admin/bids/${data.bid.id}`} style={{ ...btn, textDecoration: 'none' }}>Open the bid</a>
            <button onClick={() => pickBid(null)} style={btn}>Pick another bid</button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase' }}>All lines</span>
          {[1, 2, 3].map(n => (
            <button key={n} onClick={() => setDistrictAll(n)} style={{ ...btn, ...(district === n ? { background: DCOLOR[n], borderColor: DCOLOR[n], color: '#fff' } : {}) }}>District {n}</button>
          ))}
          {!district && <span style={{ fontSize: 12, color: '#b45309', fontWeight: 700 }}>Pick the district</span>}
          {district && data.district_guess === district && !(data.estimate && data.estimate.district) && <span style={{ fontSize: 12, color: '#6b7280' }}>from the bid's location</span>}
          <span style={{ width: 10 }} />
          {[[2, 'Last 2 yrs'], [5, 'Last 5 yrs'], [10, 'Last 10 yrs'], [0, 'All years']].map(([v, l]) => (
            <button key={v} onClick={() => changeYears(v)} style={{ ...btn, ...(years === v ? on : {}) }}>{l}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase' }}>Fill every line with</span>
          {PICKS.map(([k, l, c]) => <button key={k} onClick={() => fillAll(k)} style={{ ...btn, color: c }}>{l}</button>)}
          <span style={{ fontSize: 12, color: '#9ca3af' }}>typed prices stay as they are</span>
        </div>
      </div>

      {totals && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
          <div className="admin-card" style={{ padding: '10px 14px', flex: '1 1 180px' }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase' }}>Estimate total</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#111827' }}>{money2(totals.est)}</div>
            <div style={{ fontSize: 12, color: totals.missing ? '#b45309' : '#15803d', fontWeight: 700 }}>{totals.priced} of {data.items.length} lines priced{totals.missing ? ` · ${totals.missing} to go` : ''}</div>
          </div>
          <div className="admin-card" style={{ padding: '10px 14px', flex: '1 1 180px' }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase' }}>Our bid as priced now</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#c2410c' }}>{money2(totals.ourT)}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>{totals.est && totals.ourT ? `${totals.ourT >= totals.est ? '+' : ''}${Math.round(((totals.ourT - totals.est) / totals.est) * 100)}% vs the estimate` : 'from the bid page'}</div>
          </div>
        </div>
      )}

      <div className="admin-card" style={{ padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
          <thead><tr>
            <th style={th}>Item</th><th style={th}>Description</th><th style={{ ...th, textAlign: 'right' }}>Qty</th><th style={th}>District</th>
            <th style={th}>High</th><th style={th}>Low</th><th style={th}>Average</th>
            <th style={th}>Estimate (unit)</th><th style={{ ...th, textAlign: 'right' }}>Extended</th><th style={{ ...th, textAlign: 'right' }}>Our bid</th>
          </tr></thead>
          <tbody>{data.items.map(it => {
            const d = lineDistrict(it), s = it.stats && it.stats[d], l = lines[it.id] || {};
            const p = linePrice(it), q = (Number(it.quantity) || 0) / unitPer(it.unit);
            const moved = l.district && l.district !== district;
            return (
              <tr key={it.id} style={{ background: p == null ? '#fffbeb' : undefined }}>
                <td style={{ ...td, fontWeight: 700, whiteSpace: 'nowrap' }}>{it.item_no ? itemFmt(it.item_no) : <span style={{ color: '#9ca3af' }}>{it.pay_item_number || it.item_number || '—'}</span>}</td>
                <td style={{ ...td, minWidth: 200 }}>{it.description}<NoPricesHint it={it} d={d} years={years} onDistrict={n => setLineDistrict(it, n)} onAllYears={() => changeYears(0)} /></td>
                <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>{it.quantity ?? '—'} <span style={{ color: '#9ca3af', fontSize: 11.5 }}>{it.unit}</span></td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                  {[1, 2, 3].map(n => (
                    <button key={n} title={`District ${n}${it.stats && it.stats[n] && it.stats[n].n ? ` · ${it.stats[n].n} prices` : ' · no prices'}`} onClick={() => setLineDistrict(it, n)}
                      style={{ width: 26, height: 26, marginRight: 3, borderRadius: 6, border: `1px solid ${d === n ? DCOLOR[n] : '#e5e7eb'}`, background: d === n ? DCOLOR[n] : '#fff', color: d === n ? '#fff' : (it.stats && it.stats[n] && it.stats[n].n ? '#374151' : '#d1d5db'), fontWeight: 800, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', outline: moved && d === n ? '2px solid #fbbf24' : 'none' }}>{n}</button>
                  ))}
                </td>
                {PICKS.map(([k, , c]) => {
                  const v = statValue(s, k), chosen = l.pick === k;
                  return (
                    <td key={k} style={td}>
                      {v == null ? <span style={{ color: '#d1d5db' }}>—</span> : (
                        <button onClick={() => pick(it, k)} title={tip(s, k)}
                          style={{ padding: '4px 8px', borderRadius: 6, border: `1px solid ${chosen ? c : '#e5e7eb'}`, background: chosen ? c : '#fff', color: chosen ? '#fff' : c, fontWeight: 800, fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap' }}>{money(v)}</button>
                      )}
                      {k === 'average' && s && s.n ? <div style={{ fontSize: 10.5, color: '#9ca3af' }}>{s.n} price{s.n === 1 ? '' : 's'}</div> : null}
                    </td>
                  );
                })}
                <td style={td}>
                  <input key={`${it.id}-${l.pick}-${d}`} defaultValue={p != null ? p : ''} placeholder={s && s.n ? 'or type' : 'type a price'}
                    onBlur={e => { const v = e.target.value.replace(/[$,\s]/g, ''); if (v === '') { if (l.pick === 'manual') setLine(it.id, { pick: null, price: null }); return; } const n = Number(v); if (Number.isFinite(n) && n !== p) setLine(it.id, { pick: 'manual', price: n }); }}
                    onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                    style={{ ...inp, width: 110, padding: '5px 8px', fontWeight: 700, borderColor: l.pick === 'manual' ? '#7c3aed' : '#d1d5db', color: l.pick === 'manual' ? '#7c3aed' : '#111827' }} />
                </td>
                <td style={{ ...td, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{p != null ? money2(p * q) : '—'}</td>
                <td style={{ ...td, textAlign: 'right', color: '#c2410c', whiteSpace: 'nowrap' }}>{ours[it.id] != null ? money(Math.round(ours[it.id] * 100) / 100) : '—'}</td>
              </tr>
            );
          })}</tbody>
        </table>
      </div>
      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>Click High, Low or Average to use it (click again to clear); hover for who, where and when. A typed price shows in purple and stays put. A line moved off the bid's district has a yellow ring. Saves as you go.</div>
    </div>
  );
}
